import { AbstractO2dElement, html, customElement, property, generateShortId } from '@o2d/shared';
import type { TemplateResult, PropertyValues } from '@o2d/shared';
import { nothing } from 'lit';
import { FilterController } from '../controllers/filter.controller.js';
import { ListboxNavigationController } from '../controllers/listbox-navigation.controller.js';
import type { PopoverListItem } from '../interfaces/popover-list-item.interface.js';
import type { ItemClickedDetail, PopoverClosedDetail } from '../interfaces/event-detail.types.js';
import { ITEM_CLICKED_EVENT, POPOVER_CLOSED_EVENT } from '../interfaces/event-detail.types.js';


/**
 * Popover list web component using native Popover API and CSS Anchor Positioning.
 * Renders a filterable, clickable item list as a native popover overlay.
 */
@customElement('o2d-popover-list')
export class O2dPopoverListElement extends AbstractO2dElement {

    /** List of items to display in the popover. */
    @property({ attribute: false })
    items: PopoverListItem[] = [];

    /** Whether to show the filter input above the item list. */
    @property({ type: Boolean })
    filterable = false;

    /** Optional custom renderer for individual items. */
    @property({ attribute: false })
    itemRenderer?: (item: PopoverListItem) => TemplateResult;

    /** Optional anchor ID for CSS anchor positioning. */
    @property()
    anchorId?: string;

    /**
     * Opt-in: turns the plain list into a WAI-ARIA listbox — `listbox`/`option` roles,
     * keyboard navigation, and focus moving into the list when it opens.
     *
     * Off by default on purpose (Stefan, 2026-10-03). Six places in open2done use this
     * component today; taking focus on open would change behaviour for all of them,
     * and none of them asked for it. A consumer that wants the pattern opts in.
     */
    @property({ type: Boolean })
    listbox = false;

    /**
     * Opt-in: when the list opens, its filter input is emptied and receives focus, so the
     * user can type right away. Only acts together with {@link filterable}.
     *
     * Off by default for the same reason as {@link listbox} — taking focus on open changes
     * behaviour for every consumer, so only those that ask for it get it (Stefan,
     * 2026-10-10; requested for comboboxes with a search field).
     * Emptying happens on every device, before the list is shown, so the old text never
     * flashes; the focus follows once it is shown and is skipped for a coarse pointer
     * (touch), where it would pop up the on-screen keyboard over the list.
     */
    @property({ type: Boolean, attribute: 'focus-filter' })
    focusFilter = false;

    /**
     * Ids of the currently selected items, the source for `aria-selected`.
     *
     * Only read when {@link listbox} is on. The selection controllers keep their own
     * state; this is the element's view of it for assistive technology.
     */
    @property({ attribute: false })
    selectedIds: string[] = [];

    private _filterController = new FilterController(this);
    private _listboxController = new ListboxNavigationController(this);
    private _closedBySelection = false;
    private _closedProgrammatically = false;

    /**
     * `beforetoggle` fires synchronously before the popover is shown: with
     * {@link focusFilter}, empty the filter here so the old text and the filtered list are
     * never painted. `toggle` comes a task later and only places the focus.
     */
    private _handleBeforeToggle = (event: Event): void => {
        if ((event as ToggleEvent).newState === 'open' && this.filterable && this.focusFilter) {
            void this._resetFilter({ focus: false });
        }
    };

    /**
     * Handles the native toggle event: places the focus on open and dispatches
     * popover-closed on close.
     * Arrow function ensures stable reference for addEventListener/removeEventListener.
     */
    private _handleToggle = (event: Event): void => {
        const { newState } = event as ToggleEvent;
        if (newState === 'open') {
            void this._handleOpened();
            return;
        }
        if (newState !== 'closed') {
            return;
        }

        const reason = this._closedBySelection
            ? 'item-selected'
            : this._closedProgrammatically
                ? 'programmatic'
                : 'dismiss';
        this._closedBySelection = false;
        this._closedProgrammatically = false;

        this.fireEvent<PopoverClosedDetail>(POPOVER_CLOSED_EVENT, { reason });
    };

    /**
     * Sets popover attribute, generates anchor ID,
     * and registers the toggle event listener.
     */
    override connectedCallback(): void {
        super.connectedCallback();

        if (!this.hasAttribute('popover')) {
            this.setAttribute('popover', 'auto');
        }

        // Only generate ID and anchor if not already set by consumer (e.g., UiHelper.showContextMenu)
        if (!this.id) {
            const uuid = this.anchorId ?? generateShortId();
            this.id = `popover-${uuid}`;
            this.style.setProperty('position-anchor', `--popover-${uuid}`);
        }

        this.addEventListener('beforetoggle', this._handleBeforeToggle);
        this.addEventListener('toggle', this._handleToggle);
    }

    /**
     * Removes toggle listener to prevent memory leaks.
     */
    override disconnectedCallback(): void {
        super.disconnectedCallback();
        this.removeEventListener('beforetoggle', this._handleBeforeToggle);
        this.removeEventListener('toggle', this._handleToggle);
    }

    /**
     * Overrides native hidePopover() to track programmatic closes.
     * Sets _closedProgrammatically flag unless already flagged as item-selected.
     */
    override hidePopover(): void {
        if (!this._closedBySelection) {
            this._closedProgrammatically = true;
        }
        super.hidePopover();
    }

    /**
     * Closes the popover from a key press while keeping the reported reason `dismiss`.
     *
     * Going through {@link hidePopover} would set `_closedProgrammatically`, and the
     * `popover-closed` event would report `programmatic` — a different value than the
     * one native light-dismiss produces for the very same gesture, and part of this
     * package's public contract. Calling the native method directly avoids the
     * override without duplicating its bookkeeping.
     */
    dismissFromKeyboard(): void {
        if (this.matches(':popover-open')) {
            HTMLElement.prototype.hidePopover.call(this);
        }
    }

    /**
     * Called by selection controllers before hidePopover()
     * to flag that the close was triggered by an item selection.
     */
    notifyItemSelected(): void {
        this._closedBySelection = true;
    }

    /** Renders the filter container (if enabled) and the item list. */
    protected override render(): TemplateResult {
        const filteredItems = this._filterController.getFilteredItems(this.items);

        return html`
            ${this.filterable ? this._renderFilterContainer() : ''}
            <ul class="item-list" role=${this.listbox ? 'listbox' : nothing}>
                ${filteredItems.map(item => this._renderItem(item))}
            </ul>
        `;
    }

    /**
     * Lit calls this after every render, which is exactly when the freshly rendered
     * <li> elements exist and their colours can be written through the CSSOM. Doing it
     * here rather than in the template is what makes the component work under a
     * Content-Security-Policy without 'unsafe-inline' — see {@link _applyItemColors}.
     */
    protected override updated(changed: PropertyValues): void {
        super.updated(changed);
        this._applyItemColors();
    }

    /** Renders the filter input with clear button. */
    private _renderFilterContainer(): TemplateResult {
        return html`
            <div class="filter-container">
                <input
                    type="text"
                    placeholder="Filter..."
                    @input=${(e: InputEvent) => this._filterController.handleFilterInput(e)}
                >
                <button type="button" class="filter-clear" @click=${this._handleClearFilter}>\u2715</button>
            </div>
        `;
    }

    /**
     * Renders a single list item based on its type and state.
     * Dividers get role="separator", disabled items have no click handler.
     *
     * Clickable items carry `tabindex="-1"` so a pointerdown moves focus onto
     * the <li> itself. Without it, focus would leave the popover's anchor
     * element, firing a `focusout` with `relatedTarget = null` on the consumer
     * — which consumers (e.g. ha-combobox-multi) interpret as "focus left the
     * component" and close the popover before the click is synthesized.
     */
    private _renderItem(item: PopoverListItem): TemplateResult {
        if (item.type === 'divider') {
            // A `listbox` may only own `option` and `group` children (WAI-ARIA 1.2), so
            // inside one the divider must disappear from the accessibility tree instead
            // of announcing itself as a separator. In the plain list — the default, and
            // what every existing consumer gets — `separator` stays correct.
            return this.listbox
                ? html`<li role="presentation" aria-hidden="true" class="divider"></li>`
                : html`<li role="separator" class="divider"></li>`;
        }

        // Roles only in listbox mode; `nothing` removes the attribute entirely, so the
        // markup existing consumers receive is byte-identical to before.
        const role = this.listbox ? 'option' : nothing;
        const ariaSelected = this.listbox ? String(this.selectedIds.includes(item.id)) : nothing;
        const ariaDisabled = this.listbox && item.disabled === true ? 'true' : nothing;

        // `--item-color` lives on the <li> itself (not inside itemRenderer's own
        // markup) so it's available one level up regardless of which branch below
        // renders the item's content — consumers with a custom itemRenderer no
        // longer need to re-declare it themselves (Anke, 2026-08-20).
        //
        // It is NOT written here as a style attribute. Under a Content-Security-Policy
        // of `style-src 'self'` the browser ignores style attributes that came from
        // markup, so the colour silently never arrived — measured 2026-10-03 against
        // open2done-invoice's policy. {@link _applyItemColors} sets it through the
        // CSSOM after every render instead, which the same policy allows.
        if (item.disabled) {
            return html`<li class="item disabled" data-id="${item.id}" role=${role} aria-selected=${ariaSelected} aria-disabled=${ariaDisabled}><span class="icon">${item.icon ?? ''}</span>${item.label}</li>`;
        }

        if (this.itemRenderer) {
            return html`<li class="item" tabindex="-1" data-id="${item.id}" role=${role} aria-selected=${ariaSelected} @click=${() => this._handleItemClick(item)}>${this.itemRenderer(item)}</li>`;
        }

        return html`<li class="item" tabindex="-1" data-id="${item.id}" role=${role} aria-selected=${ariaSelected} @click=${() => this._handleItemClick(item)}><span class="icon">${item.icon ?? ''}</span>${item.label}</li>`;
    }

    /**
     * Writes each entry's `--item-color` through the CSSOM, after every render.
     *
     * A Content-Security-Policy without 'unsafe-inline' in `style-src` rejects style
     * attributes that come from markup — the attribute stays in the DOM and simply has
     * no effect, which is why the colour used to disappear without any error reaching
     * the application. The directive distinguishes by ORIGIN, not by the attribute: a
     * property set on `element.style` by script also shows up as a style attribute, but
     * applies normally (both measured 2026-10-03 in Chromium under `style-src 'self'`).
     *
     * Entries without a colour get the property REMOVED rather than skipped: Lit reuses
     * the <li> elements across renders, so a leftover value from a previous item would
     * otherwise stay behind.
     *
     * Not `AbstractO2dElement.setStyleProperty` (shared): that writes to the host
     * element, while the colour has to land on each <li>.
     */
    private _applyItemColors(): void {
        const byId = new Map(this.items.map(item => [item.id, item.color]));

        for (const li of this.querySelectorAll<HTMLLIElement>('li.item[data-id]')) {
            const color = byId.get(li.dataset['id'] ?? '');
            if (color) {
                li.style.setProperty('--item-color', color);
            } else {
                li.style.removeProperty('--item-color');
            }
        }
    }

    /**
     * Dispatches item-clicked event. Calls hidePopover() for link-type items.
     */
    private _handleItemClick(item: PopoverListItem): void {
        this.fireEvent<ItemClickedDetail>(ITEM_CLICKED_EVENT, { item });

        if (item.type === 'link') {
            this.hidePopover();
        }
    }

    /**
     * The list has just opened. With {@link focusFilter} (and a filter input, already
     * emptied in `beforetoggle`) the filter input is focused unless the pointer is coarse;
     * then it is the focus target and the listbox does not also grab focus for an option.
     * Otherwise the listbox behaviour is unchanged (`focusInitialOption` only acts with
     * `listbox` on).
     */
    private async _handleOpened(): Promise<void> {
        if (this.filterable && this.focusFilter && !globalThis.matchMedia?.('(pointer: coarse)').matches) {
            await this.updateComplete;
            this._focusFilterInput();
            return;
        }
        this._listboxController.focusInitialOption();
    }

    /** Clear button: empties the filter and puts the cursor back into the input. */
    private async _handleClearFilter(): Promise<void> {
        await this._resetFilter({ focus: true });
    }

    /**
     * Empties the filter — the controller's text AND the visible input, which is not bound
     * to it — re-renders the full list and optionally focuses the input. Shared by the
     * clear button and by opening with {@link focusFilter}.
     */
    private async _resetFilter(options: { focus: boolean }): Promise<void> {
        this._filterController.reset();
        const input = this.querySelector('.filter-container input') as HTMLInputElement | null;
        if (input) {
            input.value = '';
        }
        this.requestUpdate();
        await this.updateComplete;

        if (options.focus) {
            this._focusFilterInput();
        }
    }

    /** Puts the caret into the filter input without scrolling the page. */
    private _focusFilterInput(): void {
        (this.querySelector('.filter-container input') as HTMLInputElement | null)?.focus({ preventScroll: true });
    }

}
